import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, TrendingUp, Clock, CheckCircle } from "lucide-react";

const PIPELINE_STAGES = [
  { id: "prospect", label: "Prospect", color: "bg-slate-100 dark:bg-slate-800" },
  { id: "negotiation", label: "Negotiation", color: "bg-blue-100 dark:bg-blue-900" },
  { id: "compliance_review", label: "Compliance Review", color: "bg-yellow-100 dark:bg-yellow-900" },
  { id: "approved", label: "Approved", color: "bg-purple-100 dark:bg-purple-900" },
  { id: "active", label: "Active", color: "bg-green-100 dark:bg-green-900" },
  { id: "completed", label: "Completed", color: "bg-gray-100 dark:bg-gray-800" },
];

export default function DealPipeline() {
  const [sportFilter, setSportFilter] = useState<string>("all");

  const { data: deals, isLoading } = useQuery({
    queryKey: ["deals-pipeline", sportFilter],
    queryFn: async () => {
      let query = supabase
        .from("deals")
        .select(`
          *,
          athletes (name, sport)
        `)
        .order("created_at", { ascending: false });

      if (sportFilter !== "all") {
        query = query.eq("athletes.sport", sportFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const { data: sports } = useQuery({
    queryKey: ["sports-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("athletes")
        .select("sport")
        .order("sport");
      if (error) throw error;
      return [...new Set(data.map((a) => a.sport))];
    },
  });

  const getDealStage = (deal: any): string => {
    if (deal.status === "draft") return "prospect";
    if (deal.status === "pending_review") return "negotiation";
    if (deal.status === "pending_compliance") return "compliance_review";
    if (deal.status === "approved") return "approved";
    if (deal.status === "active") return "active";
    if (deal.status === "completed") return "completed";
    return "prospect";
  };

  const getStageDeals = (stageId: string) => {
    return deals?.filter((deal) => getDealStage(deal) === stageId) || [];
  };

  const calculateMetrics = () => {
    const totalDeals = deals?.length || 0;
    const activeDeals = deals?.filter((d) => d.status === "active").length || 0;
    const avgAmount = deals?.reduce((sum, d) => sum + Number(d.amount), 0) / totalDeals || 0;
    const conversionRate =
      totalDeals > 0 ? ((activeDeals / totalDeals) * 100).toFixed(1) : 0;

    return { totalDeals, activeDeals, avgAmount, conversionRate };
  };

  const metrics = calculateMetrics();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Deal Pipeline</h1>
            <p className="text-muted-foreground">
              Track deals through each stage of the approval process
            </p>
          </div>
          <Select value={sportFilter} onValueChange={setSportFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by sport" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sports</SelectItem>
              {sports?.map((sport) => (
                <SelectItem key={sport} value={sport}>
                  {sport}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Deals</CardTitle>
              <BarChart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.totalDeals}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.activeDeals}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Deal Value</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${metrics.avgAmount.toLocaleString()}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.conversionRate}%</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 xl:grid-cols-6">
          {PIPELINE_STAGES.map((stage) => {
            const stageDeals = getStageDeals(stage.id);
            return (
              <Card key={stage.id} className={stage.color}>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">
                    {stage.label}
                  </CardTitle>
                  <CardDescription>{stageDeals.length} deals</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {stageDeals.map((deal) => (
                    <div
                      key={deal.id}
                      className="rounded-lg border bg-card p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="font-medium text-sm line-clamp-1">
                        {deal.title}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {deal.athletes?.name}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs font-semibold text-primary">
                          ${Number(deal.amount).toLocaleString()}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {deal.athletes?.sport}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {stageDeals.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      No deals
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
